/**
 * The ordering rules are somewhat esoteric, and combine an alpha-numeric natural language order enforced by the server
 * with rules that allow newly created Notes to break that order until the whole tree is re-loaded.
 *
 * This function takes the notes from the server (originalNotes) and compares them to the esoteric client-side order,
 * pulled from a Redux slice (notePositions). It then returns an array where the originalNotes are re-ordered matched to
 *
 * @param originalNotes - array of notes returned from the server
 * @param notePositions - array of noteIds ordered by the correct clientSide order
 * @returns {Array} - an array of the reOrdered originalNotes
 */
export const reorderNotes = (originalNotes, notePositions) => {
	if (!originalNotes || typeof originalNotes === "function") return [] // if there are no original Notes, return an empty array
	if (!notePositions || typeof notePositions === "function" || notePositions.length === 0) { // if there are origional Notes, but no NoteOrder array, return origional Notes
		return originalNotes
	}

	return notePositions // in all other cases, create a new Array with each note in the correct order
		.map(noteId => {
			const noteObjIndex = originalNotes.findIndex(note => noteId === note.id)
			return originalNotes[noteObjIndex]
		})
		.filter(Boolean) // if one of the evaluated notes resolves "undefined", this ensures that down stream code doesn't error out.
}

export const breadcrumbDataToNoteIdsAsStrings = (breadCrumbString) => breadCrumbString.toString().split(".")
export const breadcrumbDataToNoteIdsAsNumbers = (breadCrumbString) => breadCrumbString.toString().split(".").map(id => parseInt(id))

/**
 * convenience function that takes a value of bytes and converts that into a human-readable filesize
 * eg: Blob.size = 65536, formatBytes(Blob.size, 2) => "64kb"
 *
 * credit: https://gist.github.com/zentala/1e6f72438796d74531803cc3833c039c
 *
 * @param bytes {number | string} - the number of bytes being converted
 * @param [decimals=2] {number}  - the number of decimal places to calculate to
 * @returns {string} - human-readable file size
 */
export function formatBytes(bytes, decimals = 2) {
	// because we're typing bytes as either number or string, we let this loose comparison ride
	if(bytes == 0) return "0 Bytes"

	let k = 1024 // binary prefix const
	let	sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] //nothing with GB can be uploaded to our server but...
	let	i = Math.floor(Math.log(bytes) / Math.log(k))

	return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i]
}